/**
 * CompletionRail — the generated checklist the Scheduling-AI draft renders
 * at the top of the create-appointment rail (Option B, LOCKED 2026-08-11,
 * 06-decisions/2026-08-11-completion-rail-architecture.md).
 *
 * Anatomy follows the Claude Design canvas "Completion Rail — card
 * vocabulary + composed states" (project 00bd05f3, judged 2026-08-11):
 * RequestHeader (orb + quoted utterance + progress), answered cards as
 * checked rows with Edit, exactly ONE open card (accent outline) carrying
 * the current ask, unresolved cards as dimmed pending rows, receipt row.
 * Book stays the panel's existing Save button — the rail never books.
 *
 * WHICH cards render and their order/state comes from the pure
 * composition engine; this file only draws and routes taps. Cards write
 * through the SAME form the manual sections use (resolution is derived
 * live from form state via useFormContext) — answering in a section
 * advances the rail automatically; there is no forked draft state.
 */

import {
  useCallback,
  useEffect,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { useFormContext } from 'react-hook-form';
import { Check, ChevronRight, X } from 'lucide-react';
import { ScripsAIIcon, type AIState } from '@/components/blocks/clinical-input/scrips-ai-icon';
import type { AppointmentFormValues } from '@/types/appointment-form';
import type { SchedulingCommandDraft } from '../scheduling-command-stub-parser';
import { deriveCompletionResolution, formatDraftWhen } from './completion-rail-derive';
import {
  composeCompletionRail,
  type CompletionCard,
  type CompletionCardKind,
} from './composition-engine';
import {
  useRailChoreography,
  type CardAuthor,
  type RailChoreography,
} from './use-rail-choreography';
import styles from './completion-rail.module.css';

/** Category label per card kind (CD sheet caption row). */
const KIND_LABEL: Record<CompletionCardKind, string> = {
  'request-header': 'Scrips Intelligence',
  patient: 'Patient',
  'register-patient': 'Register',
  practitioner: 'Doctor',
  'visit-type': 'Visit',
  'time-slot': 'Time',
  'recurrence-dates': 'Repeats',
  'chain-leg': 'Then',
  conflict: 'Conflict',
  receipt: 'Receipt',
};

/** Open-card CTA per kind — the tappable answer route. */
const OPEN_CTA: Partial<Record<CompletionCardKind, string>> = {
  patient: 'Find the patient',
  'register-patient': 'Register & continue',
  practitioner: 'Pick a doctor',
  'visit-type': 'Pick a visit type',
  'time-slot': 'Pick a time',
  'recurrence-dates': 'Confirm the dates',
  conflict: 'See free times',
};

export interface CompletionRailProps {
  draft: SchedulingCommandDraft;
  /** Downstream rejection to surface as the ConflictCard (never a toast). */
  conflict?: string;
  /** Tap routing — the PANEL owns the mapping to its views/sections. */
  onAction: (kind: CompletionCardKind) => void;
  /** The header's × — drop the AI draft, keep the manual rail as-is. */
  onDismiss?: () => void;
  /**
   * Override the header orb. The panel drives this when it knows more
   * than the rail can infer — e.g. `thinking` while a slow parse is
   * still in flight. Omitted, the rail derives it from draft arrival.
   */
  orbState?: AIState;
  /**
   * Choreography hoisted to the FORM level by the panel.
   *
   * The panel's subview early-returns unmount this component whenever a
   * picker is open, which would discard a locally-held choreography and
   * re-cascade the whole rail on return instead of resolving one card
   * (Codex P1(a)). CreateAppointmentForm therefore owns the hook and
   * passes it down. Omitted → the rail uses its own local instance and
   * behaves exactly as before, so every other caller is unaffected.
   */
  choreography?: RailChoreography;
}

export function CompletionRail({
  draft,
  conflict,
  onAction,
  onDismiss,
  orbState,
  choreography,
}: CompletionRailProps) {
  const { watch } = useFormContext<AppointmentFormValues>();
  const resolution = deriveCompletionResolution({
    patientId: watch('patientId'),
    providerId: watch('providerId'),
    appointmentProfileId: watch('appointmentProfileId'),
    slotStart: watch('slotStart'),
    slotEnd: watch('slotEnd'),
    recurringSlotCount: watch('recurring')?.slots?.length ?? 0,
    hasRecurrence: !!draft.recurrence,
    ...(conflict ? { conflict } : {}),
  });
  const cards = composeCompletionRail(draft, resolution);

  const header = cards.find((c) => c.kind === 'request-header');
  const body = cards.filter((c) => c.kind !== 'request-header');
  // Progress = answered / answerable (receipt is a defaults row, not an ask).
  const answerable = body.filter((c) => c.kind !== 'receipt');
  const doneCount = answerable.filter((c) => c.state === 'done').length;
  const when = formatDraftWhen(draft.start);

  // Who authored each change — the rule the motion layer obeys is
  // "motion means the system did this". See use-rail-choreography.ts.
  //
  // The hook is called unconditionally (hooks rules), but the PANEL's
  // hoisted instance wins when supplied: only that one survives the
  // picker subviews, which is the whole point of the prop.
  const localChoreography = useRailChoreography(cards, draft.raw);
  const choreo = choreography ?? localChoreography;

  /**
   * Rows whose enter animation has finished.
   *
   * The 0fr→1fr height animation needs `overflow: hidden` on the grid
   * child, otherwise the content spills instead of being revealed. But
   * left clipped, the open card's accent bloom (a 5px outset shadow) is
   * shorn off at the row edge. So the clip is released per row the
   * moment its own animation ends, rather than waiting for the next
   * state change to clear it.
   */
  const [settled, setSettled] = useState<{
    draftId: string | undefined;
    keys: ReadonlySet<string>;
  }>(() => ({ draftId: draft.raw, keys: new Set() }));

  // A brand-new request re-runs the cascade, so nothing is settled yet.
  // Reset DURING RENDER rather than in an effect: an effect here trips
  // react-hooks/set-state-in-effect (cascading renders) and would also
  // clear a frame late, letting the stale settled set suppress the first
  // frame of the new cascade.
  if (settled.draftId !== draft.raw) {
    setSettled({ draftId: draft.raw, keys: new Set() });
  }
  const settledKeys =
    settled.draftId === draft.raw ? settled.keys : EMPTY_SETTLED;

  const markSettled = useCallback((key: string) => {
    setSettled((prev) => {
      if (prev.keys.has(key)) return prev;
      const keys = new Set(prev.keys);
      keys.add(key);
      return { draftId: prev.draftId, keys };
    });
  }, []);

  /**
   * Has the orb's one-shot `done` animation finished for this draft?
   *
   * `isFreshDraft` stays true until the card signature next changes — so a
   * user who reads the checklist before answering anything left the mark
   * parked in `done` INDEFINITELY: brackets shut, ring hidden, motionless.
   * The state means "I just produced this", and holding it forever turns a
   * momentary announcement into a permanent, wrong resting state (Codex P3).
   *
   * The mark returns to its breathing `idle` once the animation has played
   * (0.7s in scrips-ai-icon, + a frame of slack). Reduced motion has no
   * animation to wait for, so it settles immediately.
   */
  const [orbPlayed, setOrbPlayed] = useState<{
    draftId: string | undefined;
    done: boolean;
  }>(() => ({ draftId: draft.raw, done: false }));

  // Reset during render, not in an effect — same reason as `settled` above:
  // an effect trips react-hooks/set-state-in-effect and clears a frame late.
  if (orbPlayed.draftId !== draft.raw) {
    setOrbPlayed({ draftId: draft.raw, done: false });
  }
  useEffect(() => {
    const t = window.setTimeout(
      () => setOrbPlayed((p) => (p.done ? p : { ...p, done: true })),
      ORB_DONE_MS,
    );
    return () => window.clearTimeout(t);
  }, [draft.raw]);
  const orbDonePlayed = orbPlayed.draftId === draft.raw && orbPlayed.done;

  /**
   * The header orb. Previously hard-coded to `idle`, so the surface
   * literally named "Scrips Intelligence" never once showed the
   * intelligence working — the four-state mark shipped but was never
   * driven here. `done` plays ONCE as the generated checklist lands
   * (brackets spring shut, dot pops): the mark's own way of saying
   * "I just produced this". It then rests at idle.
   * An explicit `orbState` from the panel wins when the panel knows
   * better (e.g. still thinking on a slow parse).
   */
  const derivedOrb =
    choreo.isFreshDraft && !orbDonePlayed ? 'done' : 'idle';

  return (
    <div
      className={`px-4 py-3 border-t border-border-subtle ${styles.rail}`}
      data-testid="completion-rail"
    >
      {/* RequestHeader — always first, never collapses, carries the orb. */}
      {header ? (
        <div className="mb-2">
          <div className="flex items-center gap-2">
            <ScripsAIIcon state={orbState ?? derivedOrb} size={20} />
            <p className="text-xs font-medium text-text-muted flex-1">
              Scrips Intelligence
            </p>
            {onDismiss ? (
              <button
                type="button"
                onClick={onDismiss}
                className="p-1 hover:bg-grey-100 rounded"
                aria-label="Dismiss Scrips Intelligence draft"
              >
                <X className="h-4 w-4 text-text-muted" />
              </button>
            ) : null}
          </div>
          {draft.raw ? (
            <p className="[font:var(--type-body)] text-[var(--ink-body)] mt-2">
              “{draft.raw}”
            </p>
          ) : null}
          {/* Honest-source marker (Andrew on #843): a grammar fallback must
              not read like model understanding — a partial offline parse can
              prefill a plausible WRONG draft, so say who read the sentence. */}
          {draft.source === 'grammar' ? (
            <p
              className="text-size-xs text-[var(--color-status-warning-text)] mt-1 leading-snug"
              data-testid="completion-rail-offline-marker"
            >
              Read offline — double-check every detail below.
            </p>
          ) : null}

          {/* THE MODEL'S OWN DOUBTS, rendered as asks (audit #5).
           *
           * The intent client has always computed these — the ambiguities it
           * flagged and the sub-0.85 confidence doubt — and the bar then
           * dropped them on the floor, so a 40%-confidence read looked exactly
           * as authoritative as a certain one. That is the "confidently wrong"
           * failure the audit measured 15 times in 36 utterances, and it is
           * the worst failure mode available: the user must first DETECT the
           * error before they can undo it.
           *
           * Surfacing them costs nothing new — the model already knew. This is
           * the whole of "AI that knows what it doesn't know" on the render
           * side. Deliberately QUIET: a question mark, not an alarm. The rail's
           * job is to ask, and an ask that shouts trains people to dismiss it.
           *
           * Phase 1 is header-level because the wire carries `string[]` with no
           * field attribution. When the backend upgrades to {field, question},
           * each ask moves onto its own card and this block empties itself. */}
          {draft.asks && draft.asks.length > 0 ? (
            <ul
              className="mt-2 space-y-1"
              data-testid="completion-rail-asks"
              aria-label="Questions from Scrips Intelligence"
            >
              {draft.asks.map((ask) => (
                <li
                  key={ask}
                  className="text-size-xs text-text-muted leading-snug flex gap-2"
                >
                  <span aria-hidden className="text-brand-primary">?</span>
                  <span>{ask}</span>
                </li>
              ))}
            </ul>
          ) : null}
          <div className="flex items-center gap-2 mt-2">
            <div className="flex-1 h-1 rounded-full bg-grey-200 overflow-hidden">
              <div
                className={`h-full bg-brand-primary rounded-full ${styles.progressFill}`}
                style={{
                  width: `${answerable.length > 0 ? Math.round((doneCount / answerable.length) * 100) : 0}%`,
                }}
              />
            </div>
            <span className="text-xs text-text-muted whitespace-nowrap">
              {doneCount} of {answerable.length}
            </span>
          </div>
        </div>
      ) : null}

      <div className="flex flex-col gap-2">
        {body.map((card, i) => (
          <RowShell
            key={card.key}
            index={i}
            // Animate only while this row is genuinely entering AND has
            // not already finished. The rail re-renders on every form
            // keystroke (resolution derives from `watch`), so the
            // animating class must never be re-applied on a plain
            // re-render or it would thrash.
            entering={choreo.entering.has(card.key) && !settledKeys.has(card.key)}
            author={choreo.authorOf[card.key] ?? 'system'}
            onSettled={() => markSettled(card.key)}
          >
            <CompletionCardRow
              card={card}
              when={when}
              onAction={(kind) => {
                // Tell the choreography a tap went through the RAIL, so
                // the resulting change is attributed to the system
                // advancing — not to a manual edit.
                choreo.noteRailTap(kind);
                onAction(kind);
              }}
              justResolved={choreo.justResolved.has(card.key)}
              justOpened={choreo.justOpened === card.key}
            />
          </RowShell>
        ))}
      </div>
    </div>
  );
}

/** Stable empty set for the render in which a new draft resets `settled`. */
const EMPTY_SETTLED: ReadonlySet<string> = new Set();

/** The orb's one-shot `done` animation (scrips-ai-icon: .7s) + a frame of slack. */
const ORB_DONE_MS = 760;

/** Enter duration + per-row stagger, mirroring completion-rail.module.css. */
const ROW_ENTER_MS = 220;
const ROW_STAGGER_MS = 40;

/**
 * The grid 0fr→1fr shell around one card. A card inserted mid-list
 * animates its OWN height, so the rows below travel smoothly instead of
 * teleporting — no FLIP library is needed, because this rail never
 * reorders (composition rule 1 fixes card order), it only inserts and
 * removes.
 *
 * The height animation needs `overflow: hidden` on the grid child, or the
 * content spills instead of being revealed. Left clipped, the open card's
 * accent bloom (a 5px outset shadow) would be shorn off at the row edge —
 * so the clip is released as soon as this row's entrance is done.
 *
 * WHY A TIMER AND NOT `onAnimationEnd` (2026-08-12): animationend is not
 * delivered reliably — a backgrounded or throttled tab suppresses it, and
 * measurement confirmed rows staying clipped indefinitely as a result.
 * Motion that silently fails to clean up after itself is worse than no
 * motion, so the settle is deterministic. `prefers-reduced-motion` skips
 * the wait entirely, since there is no animation to wait for.
 */
function RowShell({
  index,
  entering,
  author,
  onSettled,
  children,
}: {
  index: number;
  entering: boolean;
  author: CardAuthor;
  onSettled: () => void;
  children: ReactNode;
}) {
  useEffect(() => {
    if (!entering) return;
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      onSettled();
      return;
    }
    const ms = ROW_ENTER_MS + index * ROW_STAGGER_MS + 30;
    const t = window.setTimeout(onSettled, ms);
    return () => window.clearTimeout(t);
    // onSettled is stable per key via markSettled's useCallback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entering, index]);

  return (
    <div
      className={
        entering
          ? styles.rowShell
          : `${styles.rowShell} ${styles.rowShellSettled} ${styles.rowInstant}`
      }
      // Stagger index — the cascade reads top-down as the system
      // assembling the form.
      style={{ '--i': index } as CSSProperties}
      data-author={author}
    >
      {children}
    </div>
  );
}

function CompletionCardRow({
  card,
  when,
  onAction,
  justResolved = false,
  justOpened = false,
}: {
  card: CompletionCard;
  when: string;
  onAction: (kind: CompletionCardKind) => void;
  /** This card flipped to done on this render — land the check. */
  justResolved?: boolean;
  /** This card just became the open ask — bloom the accent once. */
  justOpened?: boolean;
}) {
  const label = KIND_LABEL[card.kind];

  if (card.state === 'done') {
    // Checked row — CD "filled" shape: green check, category, content, Edit.
    // Receipt/chain rows are informational once done; field rows reopen.
    const editable = card.kind !== 'chain-leg' && card.kind !== 'receipt';
    return (
      <div
        className="flex items-center gap-3 py-1.5"
        data-testid={`completion-card-${card.key}`}
        data-state="done"
      >
        <span
          className={`h-4 w-4 rounded-full bg-status-success flex items-center justify-center flex-shrink-0 ${
            justResolved ? styles.checkLanded : ''
          }`}
        >
          <Check className="h-3 w-3 text-white" aria-hidden />
        </span>
        <span className="text-[length:var(--type-meta-size)] font-medium text-text-muted w-14 flex-shrink-0">
          {label}
        </span>
        <span className="[font:var(--type-body)] text-[var(--ink-body)] flex-1 min-w-0 truncate">
          {card.label}
        </span>
        {editable ? (
          <button
            type="button"
            onClick={() => onAction(card.kind)}
            aria-label={`Edit ${label}`}
            className="text-xs text-brand-primary flex-shrink-0"
          >
            Edit
          </button>
        ) : null}
      </div>
    );
  }

  if (card.state === 'open') {
    // The single open ask — accent outline, question, one tappable answer.
    return (
      <div
        className={`border border-brand-primary rounded-card p-3 ${
          justOpened ? styles.openArriving : ''
        }`}
        data-testid={`completion-card-${card.key}`}
        data-state="open"
      >
        <div className="flex items-center justify-between">
          <span className="text-[length:var(--type-meta-size)] font-medium text-brand-primary">
            {label}
          </span>
          {(card.kind === 'time-slot' || card.kind === 'recurrence-dates') && when ? (
            <span className="text-xs text-text-muted">{when}</span>
          ) : null}
        </div>
        <p className="text-sm font-semibold text-text-primary mt-1 leading-snug">
          {card.label}
        </p>
        {OPEN_CTA[card.kind] ? (
          <button
            type="button"
            onClick={() => onAction(card.kind)}
            className="mt-2 w-full flex items-center justify-between text-sm text-brand-primary border border-[var(--surface-line)] rounded-card px-3 py-2 hover:bg-[var(--surface-new)]"
          >
            <span>{OPEN_CTA[card.kind]}</span>
            <ChevronRight className="h-4 w-4" aria-hidden />
          </button>
        ) : null}
      </div>
    );
  }

  // Receipt is a tap-to-review defaults row even while pending (the CD
  // sheet's expanded payer/location/SMS/reason lines live in the Payment
  // section — the tap scrolls there).
  if (card.kind === 'receipt') {
    return (
      <button
        type="button"
        onClick={() => onAction('receipt')}
        aria-label="Review booking defaults"
        className="flex items-center gap-3 py-1.5 opacity-60 w-full text-left hover:opacity-100"
        data-testid={`completion-card-${card.key}`}
        data-state="pending"
      >
        <span className="h-4 w-4 rounded-full border border-[var(--surface-line)] flex-shrink-0" />
        <span className="text-[length:var(--type-meta-size)] font-medium text-text-muted w-14 flex-shrink-0">
          {label}
        </span>
        <span className="[font:var(--type-body)] text-[var(--ink-meta)] flex-1 min-w-0 truncate">
          {card.label}
        </span>
        <ChevronRight className="h-4 w-4 text-text-muted flex-shrink-0" aria-hidden />
      </button>
    );
  }

  // Pending row — dimmed, waits its turn (tap does nothing yet; the open
  // ask advances into it).
  return (
    <div
      className="flex items-center gap-3 py-1.5 opacity-60"
      data-testid={`completion-card-${card.key}`}
      data-state="pending"
    >
      <span className="h-4 w-4 rounded-full border border-[var(--surface-line)] flex-shrink-0" />
      <span className="text-[length:var(--type-meta-size)] font-medium text-text-muted w-14 flex-shrink-0">
        {label}
      </span>
      <span className="[font:var(--type-body)] text-[var(--ink-meta)] flex-1 min-w-0 truncate">
        {card.label}
      </span>
    </div>
  );
}
